import { Link } from '@tanstack/react-router'
import styles from './cms-shell.module.css'

/**
 * Tab strip across a CMS editor.
 *
 * These are real routes, not local state, so a tab is linkable and survives a
 * reload — and, for playbooks, so the pre-existing `/schema` and `/doer-schema`
 * URLs keep working unchanged rather than becoming a tab index nobody can link
 * to. TanStack's `activeProps` marks the current one.
 *
 * `exact` belongs on the parent path only: without it that tab stays marked
 * active on every child route.
 */
export interface CmsEditorTab {
  to: string
  label: string
  exact?: boolean
}

export function CmsEditorTabs({
  tabs,
  params,
  label,
}: {
  tabs: readonly CmsEditorTab[]
  /** Route params every tab shares — the record's slug or id. */
  params: Record<string, string>
  /** Accessible name for the strip, e.g. "Playbook editor sections". */
  label: string
}) {
  return (
    <nav className={styles.tabs} aria-label={label}>
      {tabs.map((tab) => (
        <Link
          key={tab.to}
          to={tab.to}
          params={params}
          preload="intent"
          activeOptions={{ exact: !!tab.exact }}
          className={styles.tab}
          activeProps={{ className: styles.tabActive, 'aria-current': 'page' }}
        >
          {tab.label}
        </Link>
      ))}
    </nav>
  )
}
